import React, { useEffect } from 'react'
import { defineMessage, MessageDescriptor, useIntl } from 'react-intl'
import { ViewStyle } from 'react-native'
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated'

import styled, { useTheme } from 'styled-components/native'

import { SideBarIconGroup } from './SideBarIconGroup'
import { XIcon } from '../../atoms/icons/XIcon'
import { TertiaryIconButton } from '../../atoms/tertiaryIconButton/TertiaryIconButton'
import { tID } from '../../utils/utils'

const PANEL_WIDTH = 320

const OuterContainer = styled(Animated.View)(({ theme }) => ({
  flexDirection: 'column',
  width: `${PANEL_WIDTH}px`,
  position: 'absolute',
  right: 0,
  top: 0,
  bottom: 0,
  backgroundColor: theme.colors.backgroundPrimary,
}))

const Header = styled.View<{ hasLeftContent: boolean }>(({ theme, hasLeftContent }) => ({
  paddingLeft: theme.spacing['16px'],
  paddingRight: theme.spacing['16px'],
  height: '75px',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: hasLeftContent ? 'space-between' : 'flex-end',
  zIndex: 2,
}))

const Container = styled.View({
  flexDirection: 'column',
  flex: 1,
  overflow: 'scroll',
})

const CloseIconButton = styled(TertiaryIconButton)({
  alignSelf: 'center',
})

const InsertIntoMessageContainer = styled.View(({ theme }) => ({
  padding: '16px',
  borderTopWidth: '1px',
  borderTopColor: theme.colors.borderDefault,
}))

export enum ChatSideBarSelection {
  CLIENT_DETAILS = 'client_details',
  TEMPLATES = 'templates',
  SMART_SUGGESTIONS = 'smart_suggestions',
  SUMMARIES = 'summaries',
  WELLNESS_LIBRARY_ESSENTIALS = 'wellness_library_essentials',
  DOCUMENT_LIBRARY = 'document_library',
}

export type ChatRightSideBarProps = React.PropsWithChildren<{
  isOpen: boolean
  hasSelectedClient: boolean
  style?: ViewStyle
  onClose?: () => void
  itemsToShow: ChatSideBarSelection[]
  selectedItem: ChatSideBarSelection
  onItemSelected: (item: ChatSideBarSelection) => void
  insertIntoMessageButton?: React.ReactNode
  showInsertIntoMessageButton?: boolean
}>

export const TEXT_MAP: Record<ChatSideBarSelection, MessageDescriptor> = {
  [ChatSideBarSelection.CLIENT_DETAILS]: defineMessage({
    defaultMessage: 'Client Details',
    description: 'Label for client details section',
  }),
  [ChatSideBarSelection.TEMPLATES]: defineMessage({
    defaultMessage: 'Templates',
    description: 'Label for templates section',
  }),
  [ChatSideBarSelection.SMART_SUGGESTIONS]: defineMessage({
    defaultMessage: 'Smart suggestions',
    description: 'Label for smart suggestions section',
  }),
  [ChatSideBarSelection.SUMMARIES]: defineMessage({
    defaultMessage: 'Summaries',
    description: 'Label for Lyra AI summaries section',
  }),
  [ChatSideBarSelection.WELLNESS_LIBRARY_ESSENTIALS]: defineMessage({
    defaultMessage: 'Wellness Library',
    description: 'Label for wellness library',
  }),
  [ChatSideBarSelection.DOCUMENT_LIBRARY]: defineMessage({
    defaultMessage: 'Document Library',
    description: 'Label for document library',
  }),
}

export const ChatRightSideBar: React.FC<ChatRightSideBarProps> = ({
  isOpen,
  hasSelectedClient,
  style,
  onClose,
  itemsToShow,
  children,
  selectedItem,
  onItemSelected,
  insertIntoMessageButton,
  showInsertIntoMessageButton,
}) => {
  const { colors } = useTheme()
  const { formatMessage } = useIntl()

  const translateX = useSharedValue(isOpen ? 0 : PANEL_WIDTH)
  useEffect(() => {
    translateX.value = isOpen ? withTiming(0) : withTiming(PANEL_WIDTH)
  }, [isOpen, translateX])

  const contentStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: translateX.value }],
  }))

  if (!hasSelectedClient) {
    return null
  }

  const showIcons = itemsToShow.length > 1
  return (
    <OuterContainer testID={tID('ChatRightSideBar')} style={[style, contentStyle]}>
      <Header hasLeftContent={showIcons}>
        {showIcons && (
          <SideBarIconGroup itemsToShow={itemsToShow} selectedItem={selectedItem} onItemSelected={onItemSelected} />
        )}
        <CloseIconButton
          testID={tID('ChatRightSideBar-close')}
          leftIcon={<XIcon fillColor={colors.iconDefault} size={20} />}
          onPress={() => onClose?.()}
          accessibilityLabel={formatMessage({
            defaultMessage: 'Close sidebar',
            description: 'Accessibility label for close the sidebar button',
          })}
        />
      </Header>
      <Container testID={tID('ChatRightSideBar-content')}>{children}</Container>
      {selectedItem === ChatSideBarSelection.WELLNESS_LIBRARY_ESSENTIALS && showInsertIntoMessageButton && (
        <InsertIntoMessageContainer>{insertIntoMessageButton}</InsertIntoMessageContainer>
      )}
    </OuterContainer>
  )
}
